import React from "react"
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import { Link } from "gatsby"

import SEO from "../components/seo"
import Layout from '../components/layout'
import VizData from '../components/Page-Data/VisualizationData.json'
import '../CSS/WellnessTools.css'

const useStyles = makeStyles(() => ({
  root: { flexGrow: 1 },
}))

const SENTENCE_INTERVAL_MS = 9000

/* ══════════════════════════════════════════════
   Audio playback using HTML5 Audio elements
   ══════════════════════════════════════════════ */
function useAmbientAudio() {
  const audioRef = React.useRef(null)
  const mutedRef = React.useRef(false)

  const stopAll = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
  }

  const playFile = (file) => {
    stopAll()
    if (typeof window !== 'undefined') {
      const a = new window.Audio(file)
      a.loop = true
      a.volume = mutedRef.current ? 0 : 0.4
      a.play().catch(e => console.error("Audio play failed:", e))
      audioRef.current = a
    }
  }

  /* Audio files from the static/audio directory */
  const playForest = () => playFile('/audio/soundreality-nature-forest-sound-537925.mp3')
  const playOcean = () => playFile('/audio/rmultimediaeu-ocean-waves-250310.mp3')
  const playSafeSpace = () => playFile('/audio/safe-space.mp3')

  const fadeOut = () => {
    if (!audioRef.current) return
    let vol = audioRef.current.volume
    const step = vol / 20
    const t = setInterval(() => {
      if (!audioRef.current) {
        clearInterval(t)
        return
      }
      vol -= step
      if (vol <= 0) {
        vol = 0
        audioRef.current.volume = 0
        clearInterval(t)
        stopAll()
      } else {
        audioRef.current.volume = vol
      }
    }, 100)
  }

  const toggleMute = (muted) => {
    mutedRef.current = muted
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : 0.4
    }
  }

  React.useEffect(() => {
    return stopAll
  }, [])

  return { playForest, playOcean, playSafeSpace, stopAll, fadeOut, toggleMute }
}

/* Map theme id → audio play function name */
const THEME_AUDIO = {
  forest:    'playForest',
  ocean:     'playOcean',
  safespace: 'playSafeSpace',
}

/* ────────────────────────────────────────
   Themed animated backgrounds.
   Pure CSS animations — pointer-events:none.
   ──────────────────────────────────────── */
function OceanBackground() {
  const bubbles = [
    { size:8,  left:'8%',  bottom:'10%', dur:'6s',  delay:'0s'   },
    { size:12, left:'20%', bottom:'5%',  dur:'9s',  delay:'1.5s' },
    { size:6,  left:'50%', bottom:'8%',  dur:'7s',  delay:'3s'   },
    { size:10, left:'72%', bottom:'3%',  dur:'8s',  delay:'0.8s' },
    { size:7,  left:'88%', bottom:'12%', dur:'5.5s',delay:'2.2s' },
  ]
  return (
    <div className="viz-theme-bg">
      <div className="ocean-wave ocean-wave-1" />
      <div className="ocean-wave ocean-wave-2" />
      <div className="ocean-wave ocean-wave-3" />
      {bubbles.map((b, i) => (
        <div key={i} className="ocean-bubble" style={{
          width: b.size, height: b.size,
          left: b.left, bottom: b.bottom,
          animationDuration: b.dur, animationDelay: b.delay,
        }} />
      ))}
    </div>
  )
}

function ForestBackground() {
  const trees = [
    { left:'2%',  crownW:30, crownMW:42, trunkW:6, trunkH:24, dur:'5s',  delay:'0s'   },
    { left:'12%', crownW:22, crownMW:32, trunkW:5, trunkH:18, dur:'6s',  delay:'1.3s' },
    { left:'25%', crownW:34, crownMW:48, trunkW:7, trunkH:28, dur:'4.5s',delay:'0.5s' },
    { left:'62%', crownW:28, crownMW:40, trunkW:6, trunkH:22, dur:'5.5s',delay:'2s'   },
    { left:'76%', crownW:24, crownMW:35, trunkW:5, trunkH:19, dur:'6.5s',delay:'0.8s' },
    { left:'88%', crownW:20, crownMW:30, trunkW:4, trunkH:16, dur:'5s',  delay:'3s'   },
  ]
  const leaves = [
    { left:'15%', dur:'5s',  delay:'0s'   },
    { left:'30%', dur:'7s',  delay:'1.5s' },
    { left:'48%', dur:'6s',  delay:'3s'   },
    { left:'63%', dur:'8s',  delay:'0.8s' },
    { left:'80%', dur:'5.5s',delay:'2s'   },
    { left:'92%', dur:'6.5s',delay:'4s'   },
  ]
  return (
    <div className="viz-theme-bg">
      {trees.map((t, i) => (
        <div key={i} className="forest-tree" style={{ left: t.left, animationDuration: t.dur, animationDelay: t.delay }}>
          <div className="tree-crown-top" style={{ borderLeftWidth: t.crownW*0.6, borderRightWidth: t.crownW*0.6, borderBottomWidth: t.crownW }} />
          <div className="tree-crown-mid" style={{ borderLeftWidth: t.crownMW*0.6, borderRightWidth: t.crownMW*0.6, borderBottomWidth: t.crownMW }} />
          <div className="tree-trunk" style={{ width: t.trunkW, height: t.trunkH }} />
        </div>
      ))}
      {leaves.map((l, i) => (
        <div key={i} className="forest-leaf" style={{ left: l.left, top: 0, animationDuration: l.dur, animationDelay: l.delay }} />
      ))}
    </div>
  )
}

function SafeSpaceBackground() {
  const orbs = [
    { size:180, top:'-40px',  left:'-50px',  dur:'6s',  delay:'0s'   },
    { size:140, top:'-20px',  right:'-40px', dur:'8s',  delay:'1.5s' },
    { size:120, bottom:'-30px',left:'30%',   dur:'7s',  delay:'3s'   },
    { size:100, bottom:'-20px',right:'10%',  dur:'5.5s',delay:'2s'   },
  ]
  return (
    <div className="viz-theme-bg">
      <div className="safe-center-glow" />
      {orbs.map((o, i) => (
        <div key={i} className="safe-orb" style={{
          width: o.size, height: o.size,
          top: o.top, left: o.left, right: o.right, bottom: o.bottom,
          animationDuration: o.dur, animationDelay: o.delay,
        }} />
      ))}
    </div>
  )
}

function ThemeBackground({ themeId }) {
  if (themeId === 'ocean')     return <OceanBackground />
  if (themeId === 'forest')    return <ForestBackground />
  if (themeId === 'safespace') return <SafeSpaceBackground />
  return null
}

/* ══════════════════════════════════════════════════════
   Full-page Google-Doodle-style overlay.
   Covers entire viewport in the page whitespace.
   pointer-events:none — zero impact on usability.
   ══════════════════════════════════════════════════════ */
function ForestFullPage() {
  // 22 leaves spread across viewport width
  const leaves = [
    { left:'3%',  size:9,  dur:'7s',   delay:'0s'   },
    { left:'8%',  size:12, dur:'9.5s', delay:'1.4s' },
    { left:'14%', size:7,  dur:'6.5s', delay:'3s'   },
    { left:'19%', size:11, dur:'8s',   delay:'0.6s' },
    { left:'25%', size:8,  dur:'10s',  delay:'5s'   },
    { left:'31%', size:13, dur:'7.5s', delay:'2.2s' },
    { left:'37%', size:6,  dur:'8.5s', delay:'4s'   },
    { left:'43%', size:10, dur:'6s',   delay:'0.3s' },
    { left:'49%', size:14, dur:'9s',   delay:'3.8s' },
    { left:'55%', size:7,  dur:'7s',   delay:'1s'   },
    { left:'61%', size:11, dur:'11s',  delay:'2.5s' },
    { left:'67%', size:9,  dur:'6.5s', delay:'6s'   },
    { left:'72%', size:12, dur:'8s',   delay:'1.8s' },
    { left:'78%', size:8,  dur:'7.5s', delay:'4.5s' },
    { left:'83%', size:10, dur:'9.5s', delay:'0.8s' },
    { left:'88%', size:6,  dur:'6s',   delay:'3.2s' },
    { left:'92%', size:13, dur:'10s',  delay:'2s'   },
    { left:'96%', size:9,  dur:'8.5s', delay:'5.5s' },
  ]
  const trees = [
    { left:'0%',  cw:38, cmw:52, tw:7, th:30, dur:'5.5s', delay:'0s'   },
    { left:'5%',  cw:26, cmw:36, tw:5, th:20, dur:'7s',   delay:'1.5s' },
    { left:'10%', cw:32, cmw:44, tw:6, th:26, dur:'6s',   delay:'3s'   },
    { left:'87%', cw:30, cmw:42, tw:6, th:24, dur:'5s',   delay:'0.8s' },
    { left:'92%', cw:24, cmw:34, tw:5, th:18, dur:'6.5s', delay:'2s'   },
    { left:'96%', cw:20, cmw:28, tw:4, th:14, dur:'7.5s', delay:'1s'   },
  ]
  return (
    <div className="fp-overlay">
      {leaves.map((l, i) => (
        <div key={i}
          className={`fp-leaf${i % 3 === 0 ? ' fp-leaf-alt' : ''}`}
          style={{ left:l.left, width:l.size, height:l.size, animationDuration:l.dur, animationDelay:l.delay }}
        />
      ))}
      {trees.map((t, i) => (
        <div key={i} className="fp-tree" style={{ left:t.left, animationDuration:t.dur, animationDelay:t.delay }}>
          <div className="fp-tree-top" style={{ borderLeftWidth:t.cw*0.6,  borderRightWidth:t.cw*0.6,  borderBottomWidth:t.cw  }} />
          <div className="fp-tree-mid" style={{ borderLeftWidth:t.cmw*0.6, borderRightWidth:t.cmw*0.6, borderBottomWidth:t.cmw }} />
          <div className="fp-tree-trunk" style={{ width:t.tw, height:t.th }} />
        </div>
      ))}
    </div>
  )
}

function OceanFullPage() {
  const bubbles = [
    { size:7,  left:'5%',  bottom:'2%',  dur:'7s',   delay:'0s'   },
    { size:11, left:'12%', bottom:'0%',  dur:'10s',  delay:'1.5s' },
    { size:8,  left:'22%', bottom:'3%',  dur:'8.5s', delay:'3s'   },
    { size:6,  left:'33%', bottom:'1%',  dur:'6.5s', delay:'0.8s' },
    { size:13, left:'44%', bottom:'0%',  dur:'11s',  delay:'2.2s' },
    { size:9,  left:'55%', bottom:'2%',  dur:'9s',   delay:'4s'   },
    { size:7,  left:'64%', bottom:'1%',  dur:'7.5s', delay:'1s'   },
    { size:12, left:'74%', bottom:'0%',  dur:'8s',   delay:'5s'   },
    { size:8,  left:'83%', bottom:'3%',  dur:'10s',  delay:'2.5s' },
    { size:10, left:'91%', bottom:'1%',  dur:'6s',   delay:'0.3s' },
    { size:6,  left:'97%', bottom:'2%',  dur:'9.5s', delay:'3.5s' },
  ]
  return (
    <div className="fp-overlay">
      <div className="fp-wave fp-wave-1" />
      <div className="fp-wave fp-wave-2" />
      <div className="fp-wave fp-wave-3" />
      <div className="fp-wave fp-wave-4" />
      {bubbles.map((b, i) => (
        <div key={i} className="fp-bubble" style={{
          width:b.size, height:b.size,
          left:b.left, bottom:b.bottom,
          animationDuration:b.dur, animationDelay:b.delay,
        }} />
      ))}
    </div>
  )
}

function SafeSpaceFullPage() {
  const orbs = [
    { size:400, top:'-80px',   left:'-80px',   dur:'7s',  delay:'0s'   },
    { size:320, top:'-60px',   right:'-60px',  dur:'9s',  delay:'2s'   },
    { size:360, bottom:'-80px',left:'-60px',   dur:'8s',  delay:'1s'   },
    { size:280, bottom:'-60px',right:'-50px',  dur:'6.5s',delay:'3s'   },
    { size:240, top:'40%',     right:'-40px',  dur:'10s', delay:'1.5s' },
    { size:200, top:'35%',     left:'-30px',   dur:'7.5s',delay:'4s'   },
  ]
  return (
    <div className="fp-overlay">
      <div className="fp-center-glow" style={{ width:500, height:500 }} />
      {orbs.map((o, i) => (
        <div key={i} className="fp-orb" style={{
          width:o.size, height:o.size,
          top:o.top, left:o.left, right:o.right, bottom:o.bottom,
          animationDuration:o.dur, animationDelay:o.delay,
        }} />
      ))}
    </div>
  )
}

function FullPageOverlay({ themeId }) {
  if (themeId === 'forest')    return <ForestFullPage />
  if (themeId === 'ocean')     return <OceanFullPage />
  if (themeId === 'safespace') return <SafeSpaceFullPage />
  return null
}

export default function GuidedVisualization() {
  const classes = useStyles()
  const audio   = useAmbientAudio()

  const [selectedThemeId, setSelectedThemeId] = React.useState(null)
  const [isPlaying, setIsPlaying]             = React.useState(false)
  const [sentenceIndex, setSentenceIndex]     = React.useState(0)
  const [isComplete, setIsComplete]           = React.useState(false)
  const [animKey, setAnimKey]                 = React.useState(0)
  const [isMuted, setIsMuted]                 = React.useState(false)
  const [audioStarted, setAudioStarted]       = React.useState(false)

  const theme     = selectedThemeId ? VizData.themes.find(t => t.id === selectedThemeId) : null
  const sentences = theme ? theme.sentences : []

  /* Select a theme and immediately start its ambient audio */
  const handleThemeSelect = (id) => {
    setSelectedThemeId(id)
    const fn = THEME_AUDIO[id]
    if (fn && audio[fn]) {
      audio[fn]()
      setAudioStarted(true)
      setIsMuted(false)
    }
  }

  /* Sentence advance interval */
  React.useEffect(() => {
    if (!isPlaying) return
    const t = setInterval(() => {
      setSentenceIndex(prev => {
        const next = prev + 1
        if (next >= sentences.length) {
          setIsPlaying(false)
          setIsComplete(true)
          audio.fadeOut()
          return prev
        }
        setAnimKey(k => k + 1)
        return next
      })
    }, SENTENCE_INTERVAL_MS)
    return () => clearInterval(t)
  }, [isPlaying, sentences.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = () => {
    setSentenceIndex(0)
    setAnimKey(0)
    setIsComplete(false)
    setIsPlaying(true)
    /* Audio already playing from theme selection — just ensure it's on */
    if (isMuted) { setIsMuted(false); audio.toggleMute(false) }
  }

  const handlePause  = () => setIsPlaying(false)
  const handleResume = () => setIsPlaying(true)

  const handleMuteToggle = () => {
    const next = !isMuted
    setIsMuted(next)
    audio.toggleMute(next)
  }

  const handleRestart = () => {
    setIsPlaying(false)
    setIsComplete(false)
    setSentenceIndex(0)
    setAnimKey(0)
    setSelectedThemeId(null)
    setAudioStarted(false)
    setIsMuted(false)
    audio.stopAll()
  }

  return (
    <Layout>
      {/* Full-page theme overlay — Google Doodle style */}
      <FullPageOverlay themeId={selectedThemeId} />
      <SEO
        title="Guided Visualisation | Pratiiti — Mental Wellness Tools"
        description="Use Pratiiti's free guided visualisation with ambient sounds to calm your mind. Choose from Calm Forest, Ocean Breeze, or Safe Space for a soothing session."
        canonical="https://www.pratiiti.in/guided-visualization"
      />

      <div className={classes.root}>
        <Grid container justify="center">
          <Grid item xs={12} sm={10} md={8}>
            <div className="wellness-page-wrapper">

              <div className="pageheading">Guided Visualisation</div>
              <p className="wellness-section-subtitle">
                A short guided imagery session with matching ambient sounds to help you
                step away from stress and reconnect with calm. Choose a theme to begin.
              </p>

              {/* ── Theme Picker ── */}
              {!isPlaying && !isComplete && (
                <div className="wellness-card">
                  <p style={{ textAlign: 'center', color: '#555', fontSize: 14, marginBottom: 20 }}>
                    Choose your theme — ambient sound plays automatically:
                  </p>

                  <div className="viz-theme-grid">
                    {VizData.themes.map(t => (
                      <div
                        key={t.id}
                        className={`viz-theme-card${selectedThemeId === t.id ? ' selected' : ''}`}
                        onClick={() => handleThemeSelect(t.id)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={e => e.key === 'Enter' && handleThemeSelect(t.id)}
                      >
                        <span className="viz-theme-icon" role="img" aria-label={t.name}>{t.icon}</span>
                        <span className="viz-theme-name">{t.name}</span>
                        <span className="viz-theme-sound-label">
                          {t.id === 'forest'    && '🎵 Forest ambience'}
                          {t.id === 'ocean'     && '🎵 Ocean waves'}
                          {t.id === 'safespace' && '🎵 Soft tones'}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Audio preview status */}
                  {audioStarted && selectedThemeId && (
                    <div className="viz-audio-status">
                      <span role="img" aria-label="sound playing">🔊</span>
                      {' '}Ambient sound playing — {isMuted ? 'muted' : 'on'}
                      <button
                        className="viz-mute-btn"
                        onClick={handleMuteToggle}
                        aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
                      >
                        {isMuted ? '🔇 Unmute' : '🔊 Mute'}
                      </button>
                    </div>
                  )}

                  <div className="wellness-btn-group">
                    <button
                      className="wellness-btn wellness-btn-primary"
                      onClick={handleStart}
                      disabled={!selectedThemeId}
                    >
                      Begin Session
                    </button>
                  </div>

                  {!selectedThemeId && (
                    <p style={{ textAlign: 'center', color: '#aaa', fontSize: 13, marginTop: 12 }}>
                      Select a theme to begin — audio will start on selection
                    </p>
                  )}
                </div>
              )}

              {/* ── Active Session Screen ── */}
              {(isPlaying || (sentenceIndex > 0 && !isComplete && !isPlaying)) && theme && (
                <div className="wellness-card" style={{ position: 'relative', overflow: 'hidden' }}>
                  <ThemeBackground themeId={selectedThemeId} />
                  <div className="viz-content-layer">
                  {/* Theme + audio indicator */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 4 }}>
                    <span style={{ color: '#f7941e', fontSize: 13, fontWeight: 700 }}>
                      {theme.icon} {theme.name}
                    </span>
                    <button
                      className="viz-mute-btn"
                      onClick={handleMuteToggle}
                      aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
                    >
                      {isMuted ? '🔇' : '🔊'}
                    </button>
                  </div>

                  <div className="viz-session-area">
                    <p key={animKey} className="viz-sentence">
                      {sentences[sentenceIndex]}
                    </p>
                  </div>

                  {/* Dot progress */}
                  <div className="viz-progress-dots">
                    {sentences.map((_, i) => (
                      <div
                        key={i}
                        className={`viz-dot${i === sentenceIndex ? ' active' : i < sentenceIndex ? ' done' : ''}`}
                      />
                    ))}
                  </div>

                  <div className="wellness-btn-group" style={{ marginTop: 24 }}>
                    {isPlaying ? (
                      <button className="wellness-btn" onClick={handlePause}>Pause</button>
                    ) : (
                      <button className="wellness-btn wellness-btn-primary" onClick={handleResume}>
                        Resume
                      </button>
                    )}
                    <button className="wellness-btn" onClick={handleRestart}>Start Over</button>
                  </div>
                  </div>{/* end viz-content-layer */}
                </div>
              )}

              {/* ── Completion Screen ── */}
              {isComplete && (
                <div className="wellness-card">
                  <div className="viz-end-message">
                    <div style={{ fontSize: 56, marginBottom: 16 }} role="img" aria-label="theme icon">
                      {theme ? theme.icon : '🌿'}
                    </div>
                    <h3>You did beautifully.</h3>
                    <p>
                      Take a slow breath before returning to your day.<br />
                      This stillness is always available to you — come back whenever you need it.
                    </p>
                    <div className="wellness-btn-group" style={{ marginTop: 24 }}>
                      <button className="wellness-btn wellness-btn-primary" onClick={handleRestart}>
                        Try Another Theme
                      </button>
                      <Link to="/contact-us">
                        <button className="wellness-btn">Talk to a Counsellor</button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* How to use */}
              {!isPlaying && !isComplete && (
                <div className="wellness-card" style={{ marginTop: 8 }}>
                  <p style={{ color: '#003a4d', fontWeight: 700, marginBottom: 10, fontSize: 15 }}>
                    How to use this tool
                  </p>
                  <ul style={{ paddingLeft: 18, color: '#555', fontSize: 14, lineHeight: 1.9, marginBottom: 0 }}>
                    <li>Select a theme — ambient sound will begin playing immediately.</li>
                    <li>Find a quiet, comfortable place where you won't be disturbed.</li>
                    <li>Read each sentence slowly and let the imagery form in your mind.</li>
                    <li>Each sentence stays on screen for about 9 seconds. Breathe naturally throughout.</li>
                    <li>Use the mute button anytime if you prefer silence.</li>
                  </ul>
                </div>
              )}

              {/* Link to other tools */}
              {!isPlaying && (
                <div style={{ textAlign: 'center', marginTop: 32 }}>
                  <p style={{ color: '#555', fontSize: 14, marginBottom: 12 }}>Explore more wellness tools:</p>
                  <Link to="/breathing-guide">
                    <button className="wellness-btn" style={{ marginRight: 8 }}>Breathing Guide</button>
                  </Link>
                  <Link to="/wellness-quiz">
                    <button className="wellness-btn">Stress Quiz</button>
                  </Link>
                </div>
              )}

            </div>
          </Grid>
        </Grid>
      </div>
    </Layout>
  )
}
